import React, { useState, useEffect, useCallback } from 'react';

const ApiSecurityPanel = ({ isDarkMode, adminToken }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [statsLoading, setStatsLoading] = useState(false);
  const [toast, setToast] = useState({ show: false, message: '', type: '' });

  const [settings, setSettings] = useState({
    rate_limit_enabled: true,
    rate_limit_per_minute: 20,
    rate_limit_per_hour: 200,
    rate_limit_daily_guest: 50,
    global_rate_limit_per_minute: 120,
    block_suspicious_agents: true,
    require_session: false,
    min_request_interval_ms: 500,
    origin_check_enabled: false,
    enforce_ip_bans: true,
    ip_whitelist: [],
    alert_threshold: 100
  });

  const [stats, setStats] = useState({
    blocks_today: 0,
    translations_today: 0,
    top_ips: [],
    recent_blocks: []
  });

  const [whitelistText, setWhitelistText] = useState('');

  // Test Security state
  const [testRunning, setTestRunning] = useState(false);
  const [testResults, setTestResults] = useState(null);
  const [testIp, setTestIp] = useState('1.2.3.4');
  const [testAgent, setTestAgent] = useState('Mozilla/5.0');
  const [testOrigin, setTestOrigin] = useState('');

  // User Rate Limits state
  const [userLimits, setUserLimits] = useState([]);
  const [showLimitModal, setShowLimitModal] = useState(false);
  const [limitForm, setLimitForm] = useState({ email: '', translate_per_minute: 20, decoder_per_hour: 5, reason: '' });

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const headers = { 'Authorization': `Bearer ${adminToken}`, 'Content-Type': 'application/json' };

  const showToast = (message, type = 'success') => {
    setToast({ show: true, message, type });
    setTimeout(() => setToast({ show: false, message: '', type: '' }), 3000);
  };

  const loadSettings = useCallback(async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/api-security/settings`, {
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });
      if (res.ok) {
        const data = await res.json();
        setSettings(data);
        setWhitelistText((data.ip_whitelist || []).join('\n'));
      }
    } catch (err) {
      console.error('Failed to load API security settings:', err);
    } finally {
      setLoading(false);
    }
  }, [BACKEND_URL, adminToken]);

  const loadStats = useCallback(async () => {
    setStatsLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/api-security/stats`, {
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });
      if (res.ok) {
        const data = await res.json();
        setStats(data);
      }
    } catch (err) {
      console.error('Failed to load API security stats:', err);
    } finally {
      setStatsLoading(false);
    }
  }, [BACKEND_URL, adminToken]);

  const loadUserLimits = useCallback(async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/security/user-limits`, {
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });
      if (res.ok) {
        const data = await res.json();
        setUserLimits(data.user_limits || []);
      }
    } catch (err) {
      console.error('Failed to load user limits:', err);
    }
  }, [BACKEND_URL, adminToken]);

  useEffect(() => {
    loadSettings();
    loadStats();
    loadUserLimits();
  }, [loadSettings, loadStats, loadUserLimits]);

  const saveSettings = async () => {
    setSaving(true);
    try {
      const payload = {
        ...settings,
        ip_whitelist: whitelistText.split('\n').map(ip => ip.trim()).filter(Boolean)
      };
      const res = await fetch(`${BACKEND_URL}/api/admin/api-security/settings`, {
        method: 'PUT', headers, body: JSON.stringify(payload)
      });
      if (res.ok) {
        showToast('API security settings saved');
        loadSettings();
      } else {
        showToast('Failed to save settings', 'error');
      }
    } catch (err) {
      showToast('Error saving settings', 'error');
    } finally {
      setSaving(false);
    }
  };

  const runSecurityTest = async () => {
    setTestRunning(true);
    setTestResults(null);
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/api-security/test`, {
        method: 'POST', headers,
        body: JSON.stringify({ test_ip: testIp, test_agent: testAgent, test_origin: testOrigin })
      });
      if (res.ok) {
        const data = await res.json();
        setTestResults(data);
      } else {
        showToast('Test failed', 'error');
      }
    } catch (err) {
      showToast('Error running test', 'error');
    } finally {
      setTestRunning(false);
    }
  };

  const saveUserLimit = async () => {
    if (!limitForm.email.trim()) {
      showToast('Email is required', 'error');
      return;
    }
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/security/user-limits`, {
        method: 'POST', headers,
        body: JSON.stringify(limitForm)
      });
      if (res.ok) {
        showToast('User rate limit saved');
        setShowLimitModal(false);
        setLimitForm({ email: '', translate_per_minute: 20, decoder_per_hour: 5, reason: '' });
        loadUserLimits();
      } else {
        const err = await res.json();
        showToast(err.detail || 'Failed to save', 'error');
      }
    } catch (err) {
      showToast('Error saving user limit', 'error');
    }
  };

  const removeUserLimit = async (userId) => {
    if (!window.confirm('Remove rate limit for this user?')) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/security/user-limits/${userId}`, {
        method: 'DELETE', headers
      });
      if (res.ok) {
        showToast('User limit removed');
        loadUserLimits();
      } else {
        showToast('Failed to remove', 'error');
      }
    } catch (err) {
      showToast('Error removing limit', 'error');
    }
  };

  const openLimitModalForUser = (email) => {
    setLimitForm({ email: email || '', translate_per_minute: 20, decoder_per_hour: 5, reason: 'Suspicious activity detected' });
    setShowLimitModal(true);
  };

  const updateSetting = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  // Bot patterns displayed for reference (must match backend SUSPICIOUS_AGENTS)
  const botPatterns = [
    'python-requests', 'urllib', 'scrapy', 'httpclient', 'java/',
    'go-http-client', 'wget', 'curl/', 'postman', 'insomnia',
    'phantom', 'headless', 'selenium', 'puppeteer', 'playwright',
    'httrack', 'harvest', 'nikto', 'sqlmap', 'nmap',
    'masscan', 'zgrab', 'gobuster', 'dirbuster'
  ];

  // Good bots that are always allowed (search engines, social media)
  const goodBots = [
    'googlebot', 'bingbot', 'yandexbot', 'duckduckbot',
    'baiduspider', 'facebookexternalhit', 'twitterbot',
    'linkedinbot', 'whatsapp', 'telegrambot', 'applebot'
  ];

  const cardClass = `rounded-lg border p-5 ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`;
  const labelClass = `block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`;
  const inputClass = `w-full px-3 py-2 rounded-lg border text-sm ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`;
  const textMuted = isDarkMode ? 'text-gray-400' : 'text-gray-500';
  const toggleClass = "w-11 h-6 bg-gray-300 peer-focus:ring-2 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600";

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
        <span className={`ml-3 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Loading API security settings...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Toast */}
      {toast.show && (
        <div className={`fixed top-4 right-4 z-50 px-4 py-3 rounded-lg shadow-lg text-white text-sm ${
          toast.type === 'error' ? 'bg-red-500' : 'bg-green-500'
        }`}>
          {toast.message}
        </div>
      )}

      {/* Limit User Modal */}
      {showLimitModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className={`rounded-xl p-6 w-full max-w-md ${isDarkMode ? 'bg-gray-800' : 'bg-white'} shadow-2xl`}>
            <h3 className={`text-lg font-bold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              Restrict User
            </h3>
            <div className="space-y-3">
              <div>
                <label className={labelClass}>User Email</label>
                <input
                  type="email"
                  value={limitForm.email}
                  onChange={(e) => setLimitForm(p => ({ ...p, email: e.target.value }))}
                  placeholder="user@example.com"
                  className={inputClass}
                />
              </div>
              <div>
                <label className={labelClass}>Translate limit (per minute)</label>
                <input
                  type="number"
                  value={limitForm.translate_per_minute}
                  onChange={(e) => setLimitForm(p => ({ ...p, translate_per_minute: parseInt(e.target.value) || 1 }))}
                  min="1" max="1000"
                  className={inputClass}
                />
                <p className={`text-xs mt-1 ${textMuted}`}>Normal paid users have unlimited. Set low (5-20) to slow them down.</p>
              </div>
              <div>
                <label className={labelClass}>Decoder limit (per hour)</label>
                <input
                  type="number"
                  value={limitForm.decoder_per_hour}
                  onChange={(e) => setLimitForm(p => ({ ...p, decoder_per_hour: parseInt(e.target.value) || 1 }))}
                  min="1" max="100"
                  className={inputClass}
                />
                <p className={`text-xs mt-1 ${textMuted}`}>Default is 20/hr. Set lower to restrict AI decoder usage.</p>
              </div>
              <div>
                <label className={labelClass}>Reason</label>
                <input
                  type="text"
                  value={limitForm.reason}
                  onChange={(e) => setLimitForm(p => ({ ...p, reason: e.target.value }))}
                  placeholder="Suspicious scripting behavior"
                  className={inputClass}
                />
              </div>
            </div>
            <div className="flex gap-3 mt-5">
              <button
                onClick={saveUserLimit}
                className="flex-1 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-medium"
              >
                Apply Restriction
              </button>
              <button
                onClick={() => setShowLimitModal(false)}
                className={`flex-1 px-4 py-2 rounded-lg text-sm font-medium ${isDarkMode ? 'bg-gray-700 hover:bg-gray-600 text-gray-300' : 'bg-gray-100 hover:bg-gray-200 text-gray-700'}`}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className={cardClass}>
        <div className="flex items-center justify-between">
          <div>
            <h2 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              API Security
            </h2>
            <p className={`text-sm mt-1 ${textMuted}`}>
              Protect the translation API from scraping, abuse, and unauthorized access
            </p>
          </div>
          <button
            onClick={saveSettings}
            disabled={saving}
            className="px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium disabled:opacity-50"
          >
            {saving ? 'Saving...' : 'Save Settings'}
          </button>
        </div>
      </div>

      {/* Section 1: Rate Limiting */}
      <div className={cardClass}>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <span className="text-lg">Rate Limiting</span>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={settings.rate_limit_enabled}
              onChange={(e) => updateSetting('rate_limit_enabled', e.target.checked)}
              className="sr-only peer"
            />
            <div className={toggleClass}></div>
            <span className={`ml-2 text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              {settings.rate_limit_enabled ? 'Enabled' : 'Disabled'}
            </span>
          </label>
        </div>

        <div className={`grid grid-cols-1 md:grid-cols-2 gap-4 ${!settings.rate_limit_enabled ? 'opacity-50 pointer-events-none' : ''}`}>
          <div>
            <label className={labelClass}>Requests per minute (per IP)</label>
            <input
              type="number"
              value={settings.rate_limit_per_minute}
              onChange={(e) => updateSetting('rate_limit_per_minute', parseInt(e.target.value) || 1)}
              min="1" max="1000"
              className={inputClass}
            />
            <p className={`text-xs mt-1 ${textMuted}`}>Max translation requests per IP per minute (1-1000)</p>
          </div>
          <div>
            <label className={labelClass}>Requests per hour (per IP)</label>
            <input
              type="number"
              value={settings.rate_limit_per_hour}
              onChange={(e) => updateSetting('rate_limit_per_hour', parseInt(e.target.value) || 1)}
              min="1" max="10000"
              className={inputClass}
            />
            <p className={`text-xs mt-1 ${textMuted}`}>Max translation requests per IP per hour (1-10000)</p>
          </div>
          <div>
            <label className={labelClass}>Daily limit for guests</label>
            <input
              type="number"
              value={settings.rate_limit_daily_guest}
              onChange={(e) => updateSetting('rate_limit_daily_guest', parseInt(e.target.value) || 1)}
              min="1" max="1000"
              className={inputClass}
            />
            <p className={`text-xs mt-1 ${textMuted}`}>Max daily translations for non-logged-in users (1-1000)</p>
          </div>
          <div>
            <label className={labelClass}>Min interval between requests (ms)</label>
            <input
              type="number"
              value={settings.min_request_interval_ms}
              onChange={(e) => updateSetting('min_request_interval_ms', parseInt(e.target.value) || 0)}
              min="0" max="10000"
              className={inputClass}
            />
            <p className={`text-xs mt-1 ${textMuted}`}>Minimum milliseconds between requests from same IP (0 = disabled)</p>
          </div>
        </div>

        {/* Global rate limit - separate from translate-specific */}
        <div className="mt-4 pt-4 border-t border-dashed" style={{ borderColor: isDarkMode ? '#4B5563' : '#D1D5DB' }}>
          <div className="max-w-xs">
            <label className={labelClass}>Global rate limit (all API endpoints)</label>
            <input
              type="number"
              value={settings.global_rate_limit_per_minute || 120}
              onChange={(e) => updateSetting('global_rate_limit_per_minute', parseInt(e.target.value) || 10)}
              min="10" max="10000"
              className={inputClass}
            />
            <p className={`text-xs mt-1 ${textMuted}`}>
              Max total API requests per IP per minute across ALL endpoints (10-10000). Protects games, dictionary, search, etc.
            </p>
          </div>
        </div>
      </div>

      {/* Section 2: Anti-Scraping */}
      <div className={cardClass}>
        <h3 className={`text-lg font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
          Anti-Scraping
        </h3>

        <div className="space-y-4">
          {/* Block suspicious agents toggle */}
          <div className="flex items-center justify-between">
            <div>
              <span className={`text-sm font-medium ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>
                Block suspicious User-Agents
              </span>
              <p className={`text-xs ${textMuted}`}>Blocks known bots, scrapers, and automated tools</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={settings.block_suspicious_agents}
                onChange={(e) => updateSetting('block_suspicious_agents', e.target.checked)}
                className="sr-only peer"
              />
              <div className={toggleClass}></div>
            </label>
          </div>

          {/* Blocked + Allowed patterns info */}
          {settings.block_suspicious_agents && (
            <div className="space-y-3">
              <div className={`rounded-lg p-3 ${isDarkMode ? 'bg-gray-700/50' : 'bg-gray-50'}`}>
                <p className={`text-xs font-medium mb-2 ${textMuted}`}>Blocked (scrapers/attack tools):</p>
                <div className="flex flex-wrap gap-1.5">
                  {botPatterns.map(p => (
                    <span key={p} className={`px-2 py-0.5 rounded text-xs ${isDarkMode ? 'bg-red-900/40 text-red-300' : 'bg-red-50 text-red-700'}`}>
                      {p}
                    </span>
                  ))}
                </div>
              </div>
              <div className={`rounded-lg p-3 ${isDarkMode ? 'bg-gray-700/50' : 'bg-gray-50'}`}>
                <p className={`text-xs font-medium mb-2 ${textMuted}`}>Always allowed (search engines / social media):</p>
                <div className="flex flex-wrap gap-1.5">
                  {goodBots.map(p => (
                    <span key={p} className={`px-2 py-0.5 rounded text-xs ${isDarkMode ? 'bg-green-900/40 text-green-300' : 'bg-green-50 text-green-700'}`}>
                      {p}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Require session toggle */}
          <div className="flex items-center justify-between">
            <div>
              <span className={`text-sm font-medium ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>
                Require session header
              </span>
              <p className={`text-xs ${textMuted}`}>Requires X-Session-ID header (blocks raw API calls without a session)</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={settings.require_session}
                onChange={(e) => updateSetting('require_session', e.target.checked)}
                className="sr-only peer"
              />
              <div className={toggleClass}></div>
            </label>
          </div>

          {/* Origin check toggle */}
          <div className="flex items-center justify-between">
            <div>
              <span className={`text-sm font-medium ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>
                Require valid Origin header
              </span>
              <p className={`text-xs ${textMuted}`}>
                Blocks translation requests that don't come from your website domain (scripts/curl have no Origin)
              </p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={settings.origin_check_enabled || false}
                onChange={(e) => updateSetting('origin_check_enabled', e.target.checked)}
                className="sr-only peer"
              />
              <div className={toggleClass}></div>
            </label>
          </div>

          {settings.origin_check_enabled && (
            <div className={`rounded-lg p-3 ${isDarkMode ? 'bg-yellow-900/20' : 'bg-yellow-50'}`}>
              <p className={`text-xs ${isDarkMode ? 'text-yellow-300' : 'text-yellow-700'}`}>
                Only requests with an Origin header matching your ALLOWED_ORIGINS (set in backend .env) will be accepted.
                Browser requests from your website automatically include this header. Scripts and curl typically do not.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Section 3: IP Protection */}
      <div className={cardClass}>
        <h3 className={`text-lg font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
          IP Protection
        </h3>

        <div className="space-y-4">
          {/* Enforce IP bans toggle */}
          <div className="flex items-center justify-between">
            <div>
              <span className={`text-sm font-medium ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>
                Enforce IP bans on translations
              </span>
              <p className={`text-xs ${textMuted}`}>IP bans from Login Security are also checked on translation API</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={settings.enforce_ip_bans}
                onChange={(e) => updateSetting('enforce_ip_bans', e.target.checked)}
                className="sr-only peer"
              />
              <div className={toggleClass}></div>
            </label>
          </div>

          {/* Alert threshold */}
          <div className="max-w-xs">
            <label className={labelClass}>Alert threshold (per hour)</label>
            <input
              type="number"
              value={settings.alert_threshold}
              onChange={(e) => updateSetting('alert_threshold', parseInt(e.target.value) || 10)}
              min="10" max="10000"
              className={inputClass}
            />
            <p className={`text-xs mt-1 ${textMuted}`}>Log warning when an IP exceeds this many requests per hour</p>
          </div>

          {/* IP Whitelist */}
          <div>
            <label className={labelClass}>IP Whitelist (one per line)</label>
            <textarea
              value={whitelistText}
              onChange={(e) => setWhitelistText(e.target.value)}
              rows={4}
              placeholder={"192.168.1.100\n10.0.0.1"}
              className={`${inputClass} font-mono`}
            />
            <p className={`text-xs mt-1 ${textMuted}`}>
              These IPs bypass all rate limits, bot detection, and IP ban checks
            </p>
          </div>
        </div>
      </div>

      {/* Section 4: Live Stats */}
      <div className={cardClass}>
        <div className="flex items-center justify-between mb-4">
          <h3 className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
            Live Stats (Today)
          </h3>
          <button
            onClick={loadStats}
            disabled={statsLoading}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium ${isDarkMode ? 'bg-gray-700 hover:bg-gray-600 text-gray-300' : 'bg-gray-100 hover:bg-gray-200 text-gray-700'}`}
          >
            {statsLoading ? 'Refreshing...' : 'Refresh'}
          </button>
        </div>

        {/* Summary cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-5">
          <div className={`rounded-lg p-4 text-center ${isDarkMode ? 'bg-gray-700/50' : 'bg-blue-50'}`}>
            <div className={`text-2xl font-bold ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`}>
              {stats.translations_today.toLocaleString()}
            </div>
            <div className={`text-xs ${textMuted}`}>Translations served</div>
          </div>
          <div className={`rounded-lg p-4 text-center ${isDarkMode ? 'bg-gray-700/50' : 'bg-red-50'}`}>
            <div className={`text-2xl font-bold ${isDarkMode ? 'text-red-400' : 'text-red-600'}`}>
              {stats.blocks_today.toLocaleString()}
            </div>
            <div className={`text-xs ${textMuted}`}>Blocked requests</div>
          </div>
          <div className={`rounded-lg p-4 text-center ${isDarkMode ? 'bg-gray-700/50' : 'bg-green-50'}`}>
            <div className={`text-2xl font-bold ${isDarkMode ? 'text-green-400' : 'text-green-600'}`}>
              {stats.top_ips.length}
            </div>
            <div className={`text-xs ${textMuted}`}>Active IPs (last hour)</div>
          </div>
        </div>

        {/* Top IPs table */}
        {stats.top_ips.length > 0 && (
          <div className="mb-5">
            <h4 className={`text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              Top IPs (Last Hour)
            </h4>
            <div className={`rounded-lg border overflow-hidden ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              <table className="w-full text-sm">
                <thead>
                  <tr className={isDarkMode ? 'bg-gray-700' : 'bg-gray-50'}>
                    <th className={`text-left px-4 py-2 font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>#</th>
                    <th className={`text-left px-4 py-2 font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>IP Address</th>
                    <th className={`text-right px-4 py-2 font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Requests</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.top_ips.map((entry, i) => (
                    <tr key={entry.ip} className={`border-t ${isDarkMode ? 'border-gray-700' : 'border-gray-100'}`}>
                      <td className={`px-4 py-2 ${textMuted}`}>{i + 1}</td>
                      <td className={`px-4 py-2 font-mono text-xs ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>
                        {entry.ip}
                      </td>
                      <td className={`px-4 py-2 text-right font-medium ${
                        entry.requests_last_hour > settings.alert_threshold
                          ? (isDarkMode ? 'text-red-400' : 'text-red-600')
                          : (isDarkMode ? 'text-gray-200' : 'text-gray-800')
                      }`}>
                        {entry.requests_last_hour}
                        {entry.requests_last_hour > settings.alert_threshold && ' !!'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Recent blocks */}
        {stats.recent_blocks.length > 0 && (
          <div>
            <h4 className={`text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              Recent Blocks (Today)
            </h4>
            <div className={`rounded-lg border overflow-hidden overflow-x-auto ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              <table className="w-full text-sm">
                <thead>
                  <tr className={isDarkMode ? 'bg-gray-700' : 'bg-gray-50'}>
                    <th className={`text-left px-3 py-2 font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>IP</th>
                    <th className={`text-left px-3 py-2 font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>User</th>
                    <th className={`text-left px-3 py-2 font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Reason</th>
                    <th className={`text-left px-3 py-2 font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Detail</th>
                    <th className={`text-right px-3 py-2 font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Time</th>
                    <th className={`text-right px-3 py-2 font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.recent_blocks.slice(0, 15).map((block, i) => (
                    <tr key={i} className={`border-t ${isDarkMode ? 'border-gray-700' : 'border-gray-100'}`}>
                      <td className={`px-3 py-2 font-mono text-xs ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>
                        {block.ip}
                      </td>
                      <td className={`px-3 py-2 text-xs ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        {block.user_email ? (
                          <span className={`px-1.5 py-0.5 rounded ${isDarkMode ? 'bg-blue-900/40 text-blue-300' : 'bg-blue-50 text-blue-700'}`} title={block.user_email}>
                            {block.user_email.length > 20 ? block.user_email.substring(0, 20) + '...' : block.user_email}
                          </span>
                        ) : (
                          <span className={textMuted}>Guest</span>
                        )}
                      </td>
                      <td className="px-3 py-2">
                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                          block.reason === 'ip_banned' ? (isDarkMode ? 'bg-red-900/40 text-red-300' : 'bg-red-100 text-red-700') :
                          block.reason === 'suspicious_agent' ? (isDarkMode ? 'bg-orange-900/40 text-orange-300' : 'bg-orange-100 text-orange-700') :
                          block.reason === 'rate_limit' ? (isDarkMode ? 'bg-yellow-900/40 text-yellow-300' : 'bg-yellow-100 text-yellow-700') :
                          block.reason === 'too_fast' ? (isDarkMode ? 'bg-purple-900/40 text-purple-300' : 'bg-purple-100 text-purple-700') :
                          block.reason === 'bad_origin' ? (isDarkMode ? 'bg-pink-900/40 text-pink-300' : 'bg-pink-100 text-pink-700') :
                          block.reason === 'no_session' ? (isDarkMode ? 'bg-indigo-900/40 text-indigo-300' : 'bg-indigo-100 text-indigo-700') :
                          block.reason === 'global_rate_limit' ? (isDarkMode ? 'bg-yellow-900/40 text-yellow-300' : 'bg-yellow-100 text-yellow-700') :
                          block.reason === 'user_rate_override' ? (isDarkMode ? 'bg-red-900/40 text-red-300' : 'bg-red-100 text-red-700') :
                          (isDarkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-700')
                        }`}>
                          {block.reason === 'ip_banned' ? 'IP Banned' :
                           block.reason === 'suspicious_agent' ? 'Bot Detected' :
                           block.reason === 'rate_limit' ? 'Rate Limited' :
                           block.reason === 'too_fast' ? 'Too Fast' :
                           block.reason === 'bad_origin' ? 'Bad Origin' :
                           block.reason === 'no_session' ? 'No Session' :
                           block.reason === 'global_rate_limit' ? 'Global Limit' :
                           block.reason === 'user_rate_override' ? 'User Restricted' :
                           block.reason}
                        </span>
                      </td>
                      <td className={`px-3 py-2 text-xs ${textMuted} max-w-[150px] truncate`}>
                        {block.detail || block.agent || '-'}
                      </td>
                      <td className={`px-3 py-2 text-right text-xs ${textMuted}`}>
                        {block.timestamp ? new Date(block.timestamp).toLocaleTimeString() : '-'}
                      </td>
                      <td className="px-3 py-2 text-right">
                        {block.user_email && (
                          <button
                            onClick={() => openLimitModalForUser(block.user_email)}
                            className={`px-2 py-1 rounded text-xs font-medium ${isDarkMode ? 'bg-red-900/40 text-red-300 hover:bg-red-900/60' : 'bg-red-50 text-red-700 hover:bg-red-100'}`}
                            title="Restrict this user"
                          >
                            Limit
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {stats.top_ips.length === 0 && stats.recent_blocks.length === 0 && (
          <div className={`text-center py-8 ${textMuted}`}>
            <p className="text-sm">No activity recorded yet today</p>
          </div>
        )}
      </div>

      {/* Section 5: Restricted Users */}
      <div className={cardClass}>
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              Restricted Users
            </h3>
            <p className={`text-sm ${textMuted}`}>Users with custom rate limits (suspicious accounts)</p>
          </div>
          <button
            onClick={() => openLimitModalForUser('')}
            className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-medium"
          >
            + Restrict User
          </button>
        </div>

        {userLimits.length > 0 ? (
          <div className={`rounded-lg border overflow-hidden ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
            <table className="w-full text-sm">
              <thead>
                <tr className={isDarkMode ? 'bg-gray-700' : 'bg-gray-50'}>
                  <th className={`text-left px-4 py-2 font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Email</th>
                  <th className={`text-center px-4 py-2 font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Translate/min</th>
                  <th className={`text-center px-4 py-2 font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Decoder/hr</th>
                  <th className={`text-left px-4 py-2 font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Reason</th>
                  <th className={`text-left px-4 py-2 font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Date</th>
                  <th className={`text-right px-4 py-2 font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Action</th>
                </tr>
              </thead>
              <tbody>
                {userLimits.map((ul, i) => (
                  <tr key={ul.user_id || i} className={`border-t ${isDarkMode ? 'border-gray-700' : 'border-gray-100'}`}>
                    <td className={`px-4 py-2 text-sm ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>
                      {ul.email || ul.user_id}
                    </td>
                    <td className={`px-4 py-2 text-center font-medium ${isDarkMode ? 'text-yellow-300' : 'text-yellow-700'}`}>
                      {ul.translate_per_minute || '-'}
                    </td>
                    <td className={`px-4 py-2 text-center font-medium ${isDarkMode ? 'text-yellow-300' : 'text-yellow-700'}`}>
                      {ul.decoder_per_hour || '-'}
                    </td>
                    <td className={`px-4 py-2 text-xs ${textMuted}`}>
                      {ul.reason || '-'}
                    </td>
                    <td className={`px-4 py-2 text-xs ${textMuted}`}>
                      {ul.created_at ? new Date(ul.created_at).toLocaleDateString() : '-'}
                    </td>
                    <td className="px-4 py-2 text-right">
                      <button
                        onClick={() => removeUserLimit(ul.user_id)}
                        className={`px-2 py-1 rounded text-xs font-medium ${isDarkMode ? 'bg-green-900/40 text-green-300 hover:bg-green-900/60' : 'bg-green-50 text-green-700 hover:bg-green-100'}`}
                      >
                        Remove
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className={`text-center py-6 ${textMuted}`}>
            <p className="text-sm">No users are currently restricted</p>
          </div>
        )}
      </div>

      {/* Section 6: Test Security */}
      <div className={cardClass}>
        <h3 className={`text-lg font-semibold mb-1 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
          Test Security
        </h3>
        <p className={`text-sm mb-4 ${textMuted}`}>
          Simulate a request to see which security checks would pass or fail
        </p>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <div>
            <label className={labelClass}>Test IP Address</label>
            <input
              type="text"
              value={testIp}
              onChange={(e) => setTestIp(e.target.value)}
              placeholder="1.2.3.4"
              className={inputClass}
            />
          </div>
          <div>
            <label className={labelClass}>Test User-Agent</label>
            <input
              type="text"
              value={testAgent}
              onChange={(e) => setTestAgent(e.target.value)}
              placeholder="Mozilla/5.0"
              className={inputClass}
            />
            <div className="flex gap-1 mt-1">
              <button onClick={() => setTestAgent('Mozilla/5.0')} className={`text-xs px-1.5 py-0.5 rounded ${isDarkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>Browser</button>
              <button onClick={() => setTestAgent('Googlebot/2.1')} className={`text-xs px-1.5 py-0.5 rounded ${isDarkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>Google</button>
              <button onClick={() => setTestAgent('python-requests/2.28')} className={`text-xs px-1.5 py-0.5 rounded ${isDarkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>Scraper</button>
              <button onClick={() => setTestAgent('curl/7.88')} className={`text-xs px-1.5 py-0.5 rounded ${isDarkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>curl</button>
            </div>
          </div>
          <div>
            <label className={labelClass}>Test Origin</label>
            <input
              type="text"
              value={testOrigin}
              onChange={(e) => setTestOrigin(e.target.value)}
              placeholder="https://darijapro.com"
              className={inputClass}
            />
            <div className="flex gap-1 mt-1">
              <button onClick={() => setTestOrigin('')} className={`text-xs px-1.5 py-0.5 rounded ${isDarkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>Empty</button>
              <button onClick={() => setTestOrigin(window.location.origin)} className={`text-xs px-1.5 py-0.5 rounded ${isDarkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>This site</button>
              <button onClick={() => setTestOrigin('https://evil-scraper.com')} className={`text-xs px-1.5 py-0.5 rounded ${isDarkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>Fake</button>
            </div>
          </div>
        </div>

        <button
          onClick={runSecurityTest}
          disabled={testRunning}
          className={`px-4 py-2 rounded-lg text-sm font-medium ${isDarkMode ? 'bg-indigo-600 hover:bg-indigo-700 text-white' : 'bg-indigo-600 hover:bg-indigo-700 text-white'} disabled:opacity-50`}
        >
          {testRunning ? 'Testing...' : 'Run Security Test'}
        </button>

        {/* Test Results */}
        {testResults && (
          <div className="mt-4 space-y-2">
            {testResults.results.map((r, i) => (
              <div key={i} className={`flex items-center justify-between px-4 py-2.5 rounded-lg ${
                isDarkMode ? 'bg-gray-700/50' : 'bg-gray-50'
              }`}>
                <div className="flex items-center gap-3">
                  <span className={`inline-flex items-center justify-center w-7 h-7 rounded-full text-xs font-bold ${
                    r.status === 'pass' ? (isDarkMode ? 'bg-green-900/50 text-green-300' : 'bg-green-100 text-green-700') :
                    r.status === 'fail' ? (isDarkMode ? 'bg-red-900/50 text-red-300' : 'bg-red-100 text-red-700') :
                    (isDarkMode ? 'bg-gray-600 text-gray-300' : 'bg-gray-200 text-gray-500')
                  }`}>
                    {r.status === 'pass' ? 'OK' : r.status === 'fail' ? 'X' : '\u2014'}
                  </span>
                  <span className={`text-sm font-medium ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>
                    {r.check}
                  </span>
                </div>
                <span className={`text-xs ${textMuted}`}>{r.detail}</span>
              </div>
            ))}

            {testResults.allowed_origins && (
              <div className={`mt-3 px-4 py-2 rounded-lg text-xs ${isDarkMode ? 'bg-gray-700/30 text-gray-400' : 'bg-gray-50 text-gray-500'}`}>
                Allowed origins: {testResults.allowed_origins.join(', ')}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ApiSecurityPanel;
